"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAgentConversationMetadata = useAgentConversationMetadata;
exports.useConversationMetadataAggregated = useConversationMetadataAggregated;
exports.useResetAgentConversationMetadata = useResetAgentConversationMetadata;
exports.useResetConversationMetadata = useResetConversationMetadata;
// packages/shared-react/src/hooks/useMetadata.ts
const react_1 = require("react");
const recoil_1 = require("recoil");
const state_1 = require("../state");
const shared_1 = require("@heartcheck/shared");
/** ---------- Defaults & helpers ---------- */
const defaultPartialMeta = {
    agentStartResponding: null,
    agentStopResponding: null,
    agentPartialMessage: "",
    updatedAt: undefined,
};
function nowIso() {
    try {
        return new Date().toISOString();
    }
    catch {
        return undefined;
    }
}
/** Compute “is responding” for UI (never lets null/undefined leak in). */
function computeIsResponding(meta) {
    if (!meta)
        return false;
    const partial = String(meta.agentPartialMessage ?? "").trim();
    if (partial.length > 0)
        return true;
    return meta.agentStartResponding === true && meta.agentStopResponding !== true;
}
/** Parse a timestamp (string | number | undefined) to a comparable number. */
function toTs(val) {
    if (typeof val === "number" && Number.isFinite(val))
        return val;
    if (typeof val === "string") {
        const n = Date.parse(val);
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
}
/** Merge with defaults to avoid lots of optional chaining downstream. */
function normalizeAgentMeta(meta) {
    if (!meta)
        return { ...defaultPartialMeta };
    return {
        agentStartResponding: meta.agentStartResponding ?? defaultPartialMeta.agentStartResponding,
        agentStopResponding: meta.agentStopResponding ?? defaultPartialMeta.agentStopResponding,
        agentPartialMessage: typeof meta.agentPartialMessage === "string"
            ? meta.agentPartialMessage
            : String(meta.agentPartialMessage ?? ""),
        updatedAt: meta.updatedAt ?? defaultPartialMeta.updatedAt,
    };
}
/** Safe guard for the aggregated return type. */
function ensureAggregatedShape(value) {
    if (value &&
        typeof value === "object" &&
        "meta" in value &&
        Object.prototype.hasOwnProperty.call(value, "agentId")) {
        const v = value;
        return {
            meta: normalizeAgentMeta(v.meta),
            agentId: typeof v.agentId === "string" ? v.agentId : undefined,
        };
    }
    // Fallback: ALWAYS a safe object
    return { meta: { ...defaultPartialMeta }, agentId: undefined };
}
const metaSub = new shared_1.GraphqlQuery(
/* GraphQL */ `
    subscription OnAgentMetadata($conversationId: ID!) {
      onAgentMetadata(conversationId: $conversationId) {
        conversationId
        agentId
        agentStartResponding
        agentStopResponding
        agentPartialMessage
      }
    }
  `, {
    // USER_POOL auth for PUBLIC AppSync WS
    forwardAuthMode: "userpools",
});
/** Merge incoming partial fields without wiping unspecified ones. */
function mergeMeta(prev, patch) {
    const base = normalizeAgentMeta(prev);
    const next = {
        agentStartResponding: typeof patch.agentStartResponding === "boolean"
            ? patch.agentStartResponding
            : base.agentStartResponding,
        agentStopResponding: typeof patch.agentStopResponding === "boolean"
            ? patch.agentStopResponding
            : base.agentStopResponding,
        agentPartialMessage: typeof patch.agentPartialMessage === "string"
            ? patch.agentPartialMessage
            : base.agentPartialMessage,
        updatedAt: nowIso(),
    };
    return next;
}
/** ---------- Selectors ---------- */
/**
 * Read the active agent-scoped metadata for a conversation.
 * If `serverAgentId` is provided, it is used as a fallback when no activeAgentId is set.
 */
function useAgentConversationMetadata(conversationId, serverAgentId) {
    const map = (0, recoil_1.useRecoilValue)(state_1.ConversationPartialResults);
    const raw = (0, react_1.useMemo)(() => {
        if (!conversationId) {
            return {
                activeAgentId: serverAgentId ?? null,
                agentMeta: undefined,
                isResponding: false,
                partial: null,
            };
        }
        const convMeta = map[conversationId];
        const activeAgentId = convMeta?.activeAgentId ?? (serverAgentId ?? null);
        const agentMetaRaw = activeAgentId ? convMeta?.byAgent?.[activeAgentId] : undefined;
        const agentMeta = agentMetaRaw ? normalizeAgentMeta(agentMetaRaw) : undefined;
        const isResponding = computeIsResponding(agentMeta);
        const partialStr = agentMeta ? String(agentMeta.agentPartialMessage ?? "") : "";
        const partial = partialStr.trim().length > 0 ? partialStr : null;
        return { activeAgentId, agentMeta, isResponding, partial };
    }, [conversationId, serverAgentId, map]);
    // In case something weird slips through, enforce a sensible shape.
    if (!raw ||
        typeof raw !== "object" ||
        !("activeAgentId" in raw) ||
        !("isResponding" in raw) ||
        !("partial" in raw)) {
        return {
            activeAgentId: serverAgentId ?? null,
            agentMeta: undefined,
            isResponding: false,
            partial: null,
        };
    }
    return raw;
}
/**
 * Agent-agnostic: choose the most relevant metadata across all agents for this conversation,
 * and return both the meta and the agentId that produced it.
 *
 * Priority:
 *  1) If conv.activeAgentId exists and has meta → return that.
 *  2) Else the most-recent by `updatedAt`.
 *
 * This version **also** establishes a WS subscription to keep metadata live.
 * If `filterAgentId` is provided, we only surface meta for that agent (handoff-safe).
 */
function useConversationMetadataAggregated(conversationId, filterAgentId) {
    const map = (0, recoil_1.useRecoilValue)(state_1.ConversationPartialResults);
    const set = (0, recoil_1.useSetRecoilState)(state_1.ConversationPartialResults);
    // Subscribe to metadata pushes for this conversation and merge into Recoil.
    (0, react_1.useEffect)(() => {
        if (!conversationId)
            return;
        let unsubscribe;
        (async () => {
            try {
                const anySub = metaSub;
                if (typeof anySub.subscribe !== "function") {
                    // eslint-disable-next-line no-console
                    console.warn("[useMetadata] metaSub.subscribe is not a function");
                    return;
                }
                const maybeUnsub = await anySub.subscribe({ conversationId }, (payload) => {
                    const m = payload?.onAgentMetadata;
                    if (!m || m.conversationId !== conversationId)
                        return;
                    // If caller only cares about a specific agent, ignore others.
                    if (filterAgentId && m.agentId !== filterAgentId)
                        return;
                    set((prev) => {
                        const prevConv = prev[conversationId];
                        const prevByAgent = prevConv?.byAgent ?? {};
                        const prevAgentMeta = prevByAgent[m.agentId];
                        const nextAgentMeta = mergeMeta(prevAgentMeta, m);
                        const nextConv = {
                            activeAgentId: typeof prevConv?.activeAgentId === "string"
                                ? prevConv.activeAgentId
                                : prevConv?.activeAgentId ?? null,
                            byAgent: {
                                ...prevByAgent,
                                [m.agentId]: nextAgentMeta,
                            },
                        };
                        return { ...prev, [conversationId]: nextConv };
                    });
                });
                unsubscribe =
                    typeof maybeUnsub === "function"
                        ? maybeUnsub
                        : undefined;
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.warn("[useMetadata] meta subscription error", err);
            }
        })();
        return () => {
            try {
                unsubscribe?.();
            }
            catch {
                /* ignore */
            }
        };
    }, [conversationId, filterAgentId, set]);
    // Selector logic (prefers active agent; optionally filters to a specific agent)
    const rawResult = (0, react_1.useMemo)(() => {
        if (!conversationId) {
            return { meta: { ...defaultPartialMeta }, agentId: undefined };
        }
        const convMeta = map[conversationId];
        const byAgent = convMeta?.byAgent || {};
        // If we explicitly only care about one agent and it has meta, return that.
        if (filterAgentId && byAgent[filterAgentId]) {
            return {
                meta: normalizeAgentMeta(byAgent[filterAgentId]),
                agentId: filterAgentId,
            };
        }
        // Prefer the active agent if it has metadata.
        const activeId = convMeta?.activeAgentId ?? undefined;
        if (activeId && byAgent[activeId]) {
            return {
                meta: normalizeAgentMeta(byAgent[activeId]),
                agentId: activeId,
            };
        }
        // Otherwise, pick the most recently updated agent entry.
        let bestId;
        let bestMeta;
        let bestTs = -1;
        for (const [aid, m] of Object.entries(byAgent)) {
            if (!m)
                continue;
            const ts = toTs(m.updatedAt) ||
                toTs(m.lastUpdatedAt);
            if (bestMeta === undefined || ts > bestTs) {
                bestMeta = m;
                bestTs = ts;
                bestId = aid;
            }
        }
        if (bestMeta) {
            return { meta: normalizeAgentMeta(bestMeta), agentId: bestId };
        }
        // Nothing recorded yet → return safe default.
        return { meta: { ...defaultPartialMeta }, agentId: undefined };
    }, [conversationId, filterAgentId, map]);
    // FINAL GUARD: ensure we always return a proper object
    return ensureAggregatedShape(rawResult);
}
/** ---------- Mutators (soft resets) ---------- */
function useResetAgentConversationMetadata() {
    const set = (0, recoil_1.useSetRecoilState)(state_1.ConversationPartialResults);
    return (0, react_1.useCallback)((conversationId, agentId) => {
        if (!conversationId || !agentId)
            return;
        set((prev) => {
            const conv = prev[conversationId];
            if (!conv)
                return prev;
            const currentAgentMeta = conv.byAgent?.[agentId];
            if (!currentAgentMeta)
                return prev;
            const normalized = normalizeAgentMeta(currentAgentMeta);
            const nextAgentMeta = {
                ...normalized,
                agentPartialMessage: "",
                updatedAt: nowIso(),
            };
            const nextConv = {
                activeAgentId: typeof conv.activeAgentId === "string" ? conv.activeAgentId : null,
                byAgent: {
                    ...(conv.byAgent || {}),
                    [agentId]: nextAgentMeta,
                },
            };
            return { ...prev, [conversationId]: nextConv };
        });
    }, [set]);
}
function useResetConversationMetadata() {
    const set = (0, recoil_1.useSetRecoilState)(state_1.ConversationPartialResults);
    return (0, react_1.useCallback)((conversationId) => {
        if (!conversationId)
            return;
        set((prev) => {
            const conv = prev[conversationId];
            if (!conv || !conv.byAgent)
                return prev;
            const nextByAgent = {};
            for (const [aid, meta] of Object.entries(conv.byAgent)) {
                if (!meta)
                    continue;
                const normalized = normalizeAgentMeta(meta);
                nextByAgent[aid] = {
                    ...normalized,
                    agentPartialMessage: "",
                    updatedAt: nowIso(),
                };
            }
            const nextConv = {
                activeAgentId: typeof conv.activeAgentId === "string" ? conv.activeAgentId : null,
                byAgent: nextByAgent,
            };
            return { ...prev, [conversationId]: nextConv };
        });
    }, [set]);
}
